import { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { ENTITY_NAMES } from "@miplatform/constants";
import {
  IInspectionTemplatePayload,
  IOrderTemplateResponse,
  ITransferListItem,
} from "@miplatform/types";
import { simpleSort } from "@miplatform/utils";
import { TransferList } from "@mibuildweb/components";
import { useOrderTemplates } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";

export const InspectionTemplateAttachOrderTemplatesTab = ({
  form,
  extraTabProps,
}: InspectionTemplateAttachOrderTemplatesTabProps) => {
  const theme = useTheme();
  const { getOrderTemplates, orderTemplates } = useOrderTemplates();
  const { isReadOnly } = extraTabProps || {};
  const { setValue, watch } = form;
  const selectedOrderTemplates = watch("orderTemplateIds");

  const orderTemplatesMap = useMemo(() => {
    const map: Record<string, IOrderTemplateResponse> = {};
    orderTemplates?.forEach((orderTemplate) => {
      map[orderTemplate.id] = orderTemplate;
    });
    return map;
  }, [orderTemplates]);

  useEffect(() => {
    getOrderTemplates();
  }, []);

  const availableOrderTemplates = useMemo(
    () =>
      (orderTemplates || [])
        .sort((a, b) => simpleSort(a.name, b.name))
        .map((i) => ({ key: i.id, title: i.name })) as ITransferListItem[],
    [orderTemplates]
  );

  const selectedOrderTemplatesList = useMemo(
    () =>
      (selectedOrderTemplates || [])
        .map((orderTemplateId) => orderTemplatesMap[orderTemplateId])
        .filter(
          (orderTemplate): orderTemplate is NonNullable<typeof orderTemplate> =>
            Boolean(orderTemplate)
        )
        .sort((a, b) => simpleSort(a.name, b.name))
        .map((i) => ({ key: i.id, title: i.name })) as ITransferListItem[],
    [selectedOrderTemplates, orderTemplatesMap]
  );

  const handleAddItem = (key: string) => {
    const selected = orderTemplates.find((d) => d.id === key);
    if (!selected) return;
    const current = selectedOrderTemplates ?? [];
    const updated = [...current, key];
    setValue("orderTemplateIds", updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveItem = (key: string) => {
    const updated = selectedOrderTemplates?.filter((id) => id !== key);
    setValue("orderTemplateIds", updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <ConfigAccordionHeader title={`Attach ${ENTITY_NAMES.ORDER_TEMPLATES}`}>
      {availableOrderTemplates.length === 0 && (
        <div style={{ padding: theme.spacing(2) }}>
          No Order Templates Created
        </div>
      )}
      {availableOrderTemplates.length > 0 && (
        <TransferList
          showAvailableItemsSearch
          availableItems={availableOrderTemplates}
          selectedItems={selectedOrderTemplatesList}
          onAddItem={handleAddItem}
          onRemoveItem={handleRemoveItem}
          availableTitle="Available Order Templates"
          selectedTitle="Attached Order Templates"
          disabled={isReadOnly}
        />
      )}
    </ConfigAccordionHeader>
  );
};

interface InspectionTemplateAttachOrderTemplatesTabProps {
  form: UseFormReturn<IInspectionTemplatePayload>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}
